import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { blotterSlice } from '@/store/blotter/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { Order } from '@/store/orders/state.ts';
import { isTakeProfitOrder } from '@/store/orders/takeProfitOrder/helpers.ts';
import type { AppState } from '@/store/store.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectTakeProfitTile = createSelector(
  (state: AppState, tileId: string) => state.orders[tileId] as Order | undefined,
  (order) => (isTakeProfitOrder(order) ? order : undefined),
);
export const selectOrderEntryByIdFromBlotter = createSelector(
  (state: AppState, orderId: string) => blotterSlice.selectors.getOrderEntryById(state, orderId),
  (order) => order,
);

export const selectTakeProfitErrors = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return undefined;
    }

    return order.properties.errors;
  },
);

export const selectTakeProfitNotionalData = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectTakeProfitClientWayData = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      clientWay: order.properties.clientWay,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectTakeProfitNotionalCurrencyIndex = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return undefined;
    }

    return order.properties.notionalCurrencyIndex;
  },
);

export const selectTradeIdFromTakeProfitTile = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (tile, tileId) => {
    if (isNotDefined(tile) || tile.type !== 'TakeProfitOrder') {
      return undefined;
    }

    if (
      tile.properties.status.type === 'creating' ||
      tile.properties.status.type === 'failed' ||
      tile.properties.status.type === 'pending'
    ) {
      console.warn(`[TakeProfitOrderEditionTile] Tile with id ${tileId} is in creating state, no data available.`);
      return undefined;
    }

    return tile.properties.status.tradeId;
  },
);

export const selectCustomerPriceFromTakeProfitTile = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (tile) => {
    if (isNotDefined(tile) || tile.type !== 'TakeProfitOrder') {
      return undefined;
    }

    return tile.properties.customerPrice;
  },
);

export const selectNotificationEmailsTakeProfit = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (state: AppState) => state.userPreferences,
  (order, userPreference) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return [];
    }

    if (isDefined(order.properties.notificationEmails)) {
      return order.properties.notificationEmails;
    }

    return userPreference.splitNotifications ? userPreference.splitNotificationsEmailsOrders : userPreference.emails;
  },
);

export const selectSpotTakeProfitData = createSelector(
  (state: AppState, tileId: string) => selectTakeProfitTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => {
    const order = selectTakeProfitTile(state, tileId);

    if (
      isNotDefined(order) ||
      order.type !== 'TakeProfitOrder' ||
      isNotDefined(order.properties.currencyPair?.tiering) ||
      isNotDefined(order.properties.currencyPair?.currency1) ||
      isNotDefined(order.properties.currencyPair?.currency2)
    ) {
      return undefined;
    }

    const currencyPair = `${order.properties.currencyPair.currency1}/${order.properties.currencyPair.currency2}`;
    const espStreamId = getEspStreamId(currencyPair, order.properties.currencyPair.tiering);

    return state.esp.streams[espStreamId];
  },
  (order, tileId, espPrice) => {
    if (isNotDefined(order) || order.type !== 'TakeProfitOrder') {
      return undefined;
    }

    if (isNotDefined(order.properties.currencyPair?.tiering)) {
      console.warn(`[TakeProfitOrder] Tile with id ${tileId} has no tiering defined.`);
      return undefined;
    }

    return {
      spot:
        espPrice?.status === 'PRICED'
          ? convertPointsToRate(espPrice.midRate, espPrice.currencyPairPrecision)
          : undefined,
      digitCount: espPrice?.status === 'PRICED' ? espPrice.currencyPairPrecision - 1 : undefined,
    };
  },
);
